import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { webOrigin } from "@/lib/nativeBridge";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  CopyIcon as Copy,
  EnvelopeSimpleIcon as EnvelopeSimple,
  ChatCircleTextIcon as ChatCircleText,
  DogIcon as Dog,
  HouseLineIcon as HouseHeart,
  ScissorsIcon as Scissors,
  StorefrontIcon as Storefront,
  HeartStraightIcon as HeartStraight,
  LinkIcon as LinkIcon2,
  AppleLogoIcon as AppleLogo,
  GooglePlayLogoIcon as GooglePlayLogo,
} from "@phosphor-icons/react";

const IOS_URL = "https://apps.apple.com/us/app/armstrong-k9-registry/id6790544132";
const ANDROID_URL = "https://play.google.com/store/apps/details?id=com.armstrongk9registry";

const appBlock = () =>
  `Get the app — it takes 30 seconds:\n` +
  `• iPhone/iPad: ${IOS_URL}\n` +
  `• Android: ${ANDROID_URL}\n` +
  `• Or on any computer: armstrongk9registry.com — one account works everywhere.`;

function buildTemplates(refUrl) {
  const site = refUrl;
  return [
    {
      key: "breeders",
      label: "Breeders",
      icon: Dog,
      subject: "Your kennel deserves a real home online",
      email:
`Hi [Name],

I built Armstrong K9 Registry because breeders like you deserve better than Facebook posts that disappear. This is a true home for your program — pedigrees, litters, buyers, and payments in one beautiful place.

What you get:
• Registered pedigrees with printable certificates and a public kennel page that sells your program for you
• Litter marketplace — list every puppy for 60 days ($19.99/litter); buyers inquire directly
• Live video tours — buyers pay $35 to meet your dogs on camera; you keep 60–80% of every tour
• Heat-cycle tracking + a breeding directory to plan and promote pairings
• Escrow-protected deposits so you never chase payments again

Plans (pricing locked for life):
• Breeder Basic — $19/mo or $190/yr: 10 dogs + 4 litters/month, kennel profile, heat tracker
• Breeder Pro — $44/mo or $420/yr: unlimited everything, free pedigree certificates, Verified Pro seal, Dog Log included
• Kennel Elite — $129/mo or $1,290/yr: Pro + analytics, staff seats, featured placement, priority support
• Founding Elite: the first 100 kennels lock in Elite at $99/mo for 3 years

Set up in 10 minutes:
1. Join here: ${site}
2. Pick "Breeder" and create your kennel page
3. Add your dogs, connect payouts (2 minutes), publish your first litter

Manage it all from the app — litter inquiries, tour bookings, and buyer messages in your pocket with Face ID sign-in.
${appBlock()}

Your dogs are your legacy. Let's make it look like one.
— Lauren, Armstrong K9 Registry`,
      sms: `Hey [Name]! I launched Armstrong K9 Registry — pedigrees, litter listings, live video tours (you keep up to 80%), and escrow deposits, all in one place. Plans from $19/mo; first 100 Elite kennels lock $99/mo for 3 years. Join: ${refUrl}`,
    },
    {
      key: "buyers",
      label: "Puppy Buyers",
      icon: HouseHeart,
      subject: "Find your puppy the safe way",
      email:
`Hi [Name],

Looking for a puppy shouldn't feel like dodging scams. Armstrong K9 Registry is where verified breeders list real, pedigreed litters — and every step protects you.

Free to browse: every litter and kennel, verified badges, real pedigrees, direct messaging.

What makes it safe:
• Live video tours ($35) — meet the puppy, the mom, and the breeder on camera before you commit
• Escrow deposits — your money is held safely and released only as the sale completes
• DNA-Verified badges — parentage backed by actual DNA panels, not promises

Buyer Pass — $4.99/mo or $39/yr: unlimited inquiries, watchlists, saved searches, and instant alerts the moment your breed is listed. Example: set an alert for "French Bulldog within 200 miles" and get pinged the day a litter drops.

Start in 5 minutes:
1. Join here: ${site}
2. Pick your breed and set an alert
3. Watch litters you love; book a video tour when you're ready

Puppy alerts hit your phone the moment a litter drops — grab the app so you never miss one.
${appBlock()}

Your next best friend is already out there.
— Lauren, Armstrong K9 Registry`,
      sms: `Hey [Name]! If you're puppy hunting — Armstrong K9 Registry has verified breeders, real pedigrees, live video tours before you commit, and escrow-protected deposits. Free to browse: ${refUrl}`,
    },
    {
      key: "pros",
      label: "Service Pros",
      icon: Scissors,
      subject: "Your clients are already here — get booked",
      email:
`Hi [Name],

I'd love to feature you on Armstrong K9 Registry's Find-a-Pro directory — where dog owners actively looking for grooming, training, walking, and sitting can find and book you instantly.

What you get:
• Free to join and list — no monthly fee, ever
• Your own bookable page — services, prices, photos, reviews, availability
• Clients pay online; money goes straight to your bank (about 2-day payouts). You keep 88% — no invoicing, no chasing payments
• Recurring bookings (up to 26 sessions) for weekly clients
• Trust badges that lift your ranking: identity verification (free), insurance, Pet First Aid, and an optional background check ($39 — $25 comes right back after your first booking)

Example: set a $100 groom → client pays $103 → you receive $88 in your bank two days later. No invoice, ever.

Set up in 15 minutes:
1. Join here: ${site}
2. Choose "Service Provider" and build your profile + services
3. Connect payouts, get approved, start taking bookings

New booking requests buzz your phone instantly — accept a client right from the grooming table.
${appBlock()}

Fill your calendar with dog people.
— Lauren, Armstrong K9 Registry`,
      sms: `Hey [Name]! Free listing for your business on Armstrong K9 Registry — clients book & pay online, you keep 88%, payouts hit your bank automatically. 15-min setup: ${refUrl}`,
    },
    {
      key: "sellers",
      label: "Storefronts",
      icon: Storefront,
      subject: "Open your Dog Shop — your brand, your prices, your payouts",
      email:
`Hi [Name],

Armstrong K9 Registry members buy dog food, treats, and gear every week — and you can be their shop. The Dog Shop gives you your own branded store page.

What you get for $19/mo (or $179/yr):
• Your own store with your logo, colors, photos, and prices
• Sell food (local delivery zones you control) and merch
• Payouts go straight to your bank — just a 5% platform fee per sale
• Optional Featured slot ($29/30 days) pins you to the top of the directory

Example: a kennel selling their own food blend to local families clears the monthly fee with one order.

Set up in 20 minutes:
1. Join here: ${site}
2. Open "My Storefront" and add products + delivery ZIPs
3. Connect payouts and share your store link everywhere

Order notifications land on your phone the second someone buys.
${appBlock()}

Turn your followers into customers.
— Lauren, Armstrong K9 Registry`,
      sms: `Hey [Name]! You can open your own branded Dog Shop on Armstrong K9 Registry — $19/mo, payouts straight to your bank, you set the prices. 20-min setup: ${refUrl}`,
    },
    {
      key: "owners",
      label: "Dog Owners",
      icon: HeartStraight,
      subject: "One app for your whole dog life",
      email:
`Hi [Name],

Even if you never breed or buy, Armstrong K9 Registry is built for you:

• The social feed — share your dog's moments with people who actually care about dogs
• Dog Log Care Hub ($9.99/mo or $69/yr) — track meals, walks, meds, weight, and symptoms; export Vet Visit reports and shareable Pet Sitter instructions
• Events — shows, meetups, and competitions near you
• Find-a-Pro — vetted groomers, trainers, walkers, and sitters you can book and pay in two taps
• Pedigree lookups, breed pages, and in-app messaging — all included free

Start free in 2 minutes:
1. Join here: ${site}
2. Add your dog
3. Post their first photo — that's it

This one lives on your phone — log walks on the trail, post from the park, book a groomer from the couch.
${appBlock()}

Every dog deserves a registry.
— Lauren, Armstrong K9 Registry`,
      sms: `Hey [Name]! Made something for dog people — Armstrong K9 Registry: a dog social feed, care tracking, vetted groomers/trainers you can book online, and events near you. Free to join: ${refUrl}`,
    },
  ];
}

const copyText = async (text, label) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied — ready to paste`);
  } catch {
    toast.error("Couldn't copy — long-press to select instead");
  }
};

export default function AdminShareInvite() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [refCode, setRefCode] = useState(null);
  const [tab, setTab] = useState("breeders");

  useEffect(() => {
    if (!isAdmin) return;
    api.post("/breeder-onboarding/referral-link").then((r) => setRefCode(r.data?.code || null)).catch(() => setRefCode(null));
  }, [isAdmin]);

  if (user && !isAdmin) return <Navigate to="/dashboard" replace />;

  const refUrl = refCode ? `${webOrigin()}/register?ref=${refCode}` : `${webOrigin()}/register`;
  const templates = buildTemplates(refUrl);
  const active = templates.find((t) => t.key === tab) || templates[0];

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="share-invite-page">
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-1">Grow the registry</p>
        <h1 className="font-spectral text-4xl tracking-tight">Share &amp; Invite</h1>
        <p className="text-sm text-[#6B6B6B] mt-2 max-w-2xl leading-relaxed">
          Pre-written outreach for every persona — current pricing, setup steps, and your personal
          referral link already baked in. Tap copy, paste into email or text, send. Signups through
          these links are credited to you.
        </p>

        {/* Referral + app links strip */}
        <div className="mt-6 bg-ink text-white rounded-xl border border-gold/40 p-5" data-testid="share-invite-links">
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-gold uppercase tracking-[0.18em]">
              <LinkIcon2 size={14} /> Your invite link
            </span>
            <code className="text-[13px] bg-white/10 rounded-lg px-3 py-1.5 break-all" data-testid="share-invite-ref-url">{refUrl}</code>
            <Button size="sm" variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white text-xs"
              onClick={() => copyText(refUrl, "Invite link")} data-testid="copy-ref-link-btn">
              <Copy size={12} className="mr-1" /> Copy
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-3 pt-3 border-t border-white/15 text-[12.5px] text-white/80">
            <button onClick={() => copyText(IOS_URL, "App Store link")} className="inline-flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="copy-ios-link">
              <AppleLogo size={14} weight="fill" /> App Store link <Copy size={11} />
            </button>
            <button onClick={() => copyText(ANDROID_URL, "Google Play link")} className="inline-flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="copy-android-link">
              <GooglePlayLogo size={14} weight="fill" /> Google Play link <Copy size={11} />
            </button>
            <button onClick={() => copyText(appBlock(), "App download block")} className="inline-flex items-center gap-1.5 hover:text-gold transition-colors" data-testid="copy-app-block">
              Copy full app block <Copy size={11} />
            </button>
          </div>
        </div>

        {/* Persona tabs */}
        <div className="flex items-center gap-1.5 mt-8 flex-wrap" data-testid="share-invite-tabs">
          {templates.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-semibold border transition-colors ${
                tab === t.key ? "bg-ink text-white border-ink" : "bg-white text-[#6B6B6B] border-[#E5E5E5] hover:border-ink"
              }`}
              data-testid={`share-tab-${t.key}`}>
              <t.icon size={14} weight="duotone" /> {t.label}
            </button>
          ))}
        </div>

        {/* Email card */}
        <div className="mt-5 bg-white border border-[#EAEAEA] rounded-xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="share-email-card">
          <div className="p-5 border-b border-[#F0F0F0] flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <EnvelopeSimple size={18} weight="duotone" className="text-gold-dark" />
              <h2 className="font-spectral text-xl">Email</h2>
            </div>
            <Button size="sm" className="bg-ink hover:bg-crimson text-white rounded-full text-xs"
              onClick={() => copyText(`Subject: ${active.subject}\n\n${active.email}`, "Full email")}
              data-testid="copy-full-email-btn">
              <Copy size={12} className="mr-1" /> Copy full email
            </Button>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-3 bg-cream/50 border border-[#E2DAC2] rounded-lg px-4 py-2.5">
              <p className="text-[13.5px]"><span className="text-[#8A6A00] font-semibold text-[11px] uppercase tracking-wider mr-2">Subject</span>{active.subject}</p>
              <button onClick={() => copyText(active.subject, "Subject")} className="text-[#8A6A00] hover:text-ink shrink-0" data-testid="copy-subject-btn"><Copy size={14} /></button>
            </div>
            <pre className="whitespace-pre-wrap font-sans text-[13.5px] leading-relaxed text-[#333] bg-[#FBFAF7] border border-[#EFEBE0] rounded-lg p-4 max-h-[420px] overflow-y-auto" data-testid="email-body-preview">{active.email}</pre>
          </div>
        </div>

        {/* SMS card */}
        <div className="mt-5 bg-white border border-[#EAEAEA] rounded-xl p-5 shadow-[0_8px_30px_rgba(0,0,0,0.04)]" data-testid="share-sms-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <ChatCircleText size={18} weight="duotone" className="text-gold-dark" />
              <h2 className="font-spectral text-xl">Text message</h2>
            </div>
            <Button size="sm" variant="outline" className="rounded-full text-xs"
              onClick={() => copyText(active.sms, "Text message")} data-testid="copy-sms-btn">
              <Copy size={12} className="mr-1" /> Copy text
            </Button>
          </div>
          <p className="mt-3 text-[13.5px] leading-relaxed text-[#333] bg-[#FBFAF7] border border-[#EFEBE0] rounded-lg p-4" data-testid="sms-preview">{active.sms}</p>
        </div>

        <p className="text-[11.5px] text-[#8A8477] mt-6 leading-relaxed">
          Tip: personalize the [Name] and one detail about their dogs before sending — replies triple
          when it reads like it was written just for them. Memberships are best value on the website;
          the apps add Face ID sign-in and push alerts. One account works everywhere.
        </p>
      </div>
    </AppShell>
  );
}
